import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} NotificationData
 * @property {string} title - Notification title
 * @property {string} description - Notification description/content
 * @property {number[]} sub_school_ids - Array of sub-school institution IDs
 * @property {string[]} [role_slug] - Optional array of role slugs to send notification to
 * @property {File[]} [media_files] - Optional array of media files to attach
 */

/**
 * @description Custom hook to send notifications from the organization admin
 */
export const useSendNotificationOrganization = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * @description Send a notification to the specified recipients
     * @async
     * @param {NotificationData} notificationData - The notification data
     * @param {() => void} [onSuccess] - Callback function to be called on success
     * @returns {Promise<boolean>} Resolves with true on success, false on failure
     */
    const sendNotification = async (notificationData, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            const formData = new FormData();
            formData.append("title", notificationData.title);
            formData.append("description", notificationData.description);

            // Append each sub_school_id individually
            notificationData.sub_school_ids.forEach((id) => {
                formData.append("sub_school_ids[]", id);
            });

            // Append each role_slug if provided
            if (notificationData.role_slug && notificationData.role_slug.length > 0) {
                notificationData.role_slug.forEach((slug) => {
                    formData.append("role_slug[]", slug);
                });
            }

            // Append media files if provided
            if (notificationData.media_files && notificationData.media_files.length > 0) {
                notificationData.media_files.forEach((file) => {
                    formData.append("media_files[]", file);
                });
            }

            await axiosInstance.post(
                `${TENANT_DOMAIN}/organization/admin/create/annoucement`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                },
            );

            setSuccess(true);
            if (onSuccess) onSuccess();
            return true;
        } catch (err) {
            setSuccess(false);

            if (err?.response?.status === 500 || err?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

                setError(
                    firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
                );
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, success, sendNotification };
};